import { Card, IconButton, Typography } from '@mui/material';
import { Box, styled } from '@mui/system';

import { SERIF_STACK } from '../tokens';
import { FACTS_CLASS, factClass } from './useFactsFit';

// The card root. Sized entirely by its grid cell — the grid decides how big a
// card is, the card only decides how to fill it.
export const EntityCard = styled(Card)(() => ({
  height: '100%',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  borderRadius: 8,
  '&:hover': {
    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
  },
}));

// The media frame's share of card height. Only a frame with a shape to keep
// takes the ceiling — the item photo's 16:9; entity media is the leftover.
export const MEDIA_MIN_HEIGHT = '35%';
export const MEDIA_MAX_HEIGHT = '60%';

// The portrait / logo / cover area. Takes all the space the identity block
// doesn't, down to the floor. A zero basis rather than `auto`: the media's own
// size must not decide how much of the card it gets.
export const MediaWrap = styled(Box)(({ theme }) => ({
  position: 'relative',
  flex: '1 1 0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(1),
  minHeight: MEDIA_MIN_HEIGHT,
  overflow: 'hidden',
}));

export const IdentityStack = styled(Box)(({ theme }) => ({
  flex: '0 0 auto',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  padding: theme.spacing(0.5, 1.5, 0.25),
  overflow: 'hidden',
  // useFactsFit's verdict: the facts it shed, and the line itself when even
  // the count won't fit.
  [`&[data-shed~="age"] .${factClass('age')}`]: { display: 'none' },
  [`&[data-shed~="needed"] .${factClass('needed')}`]: { display: 'none' },
  [`&[data-shed~="reviewed"] .${factClass('reviewed')}`]: { display: 'none' },
  [`&[data-shed~="all"] .${FACTS_CLASS}`]: { display: 'none' },
}));

// Scales with the card: at h6 throughout, a small card's title swamps its
// picture. `cqw` reads the grid cell, the nearest sized container.
export const CardTitle = styled(Typography)(({ theme }) => ({
  fontFamily: SERIF_STACK,
  fontWeight: 'bold',
  fontSize: `clamp(0.95rem, 7cqw, ${theme.typography.h6.fontSize})`,
  lineHeight: 1.2,
  cursor: 'pointer',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  // A title can be a bare URL, which has nowhere to break on its own.
  overflowWrap: 'anywhere',
}));

// The facts line under the title - a ` · `-joined string the card assembles.
// One line or none, which is what makes the overflow measurable.
export const CardSubheader = styled(Typography)(({ theme }) => ({
  textTransform: 'lowercase',
  fontSize: theme.typography.body2.fontSize,
  color: theme.palette.text.secondary,
  // Between the title and itself, so a card with no facts line keeps neither.
  marginTop: theme.spacing(0.25),
  marginBottom: theme.spacing(0.25),
  maxWidth: '100%',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
}));

export const CardExcerpt = styled(Typography)({
  fontFamily: SERIF_STACK,
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

// The footer's right half, and the whole of it since the metric chips left.
export const ActionsBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  marginLeft: 'auto',
}));

export const VerdictRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  padding: theme.spacing(1),
  borderTop: '1px solid #f0f0f0',
}));

export const CompactActionButton = styled(IconButton)({
  padding: 4,
  borderRadius: 6,
  color: '#3c3c3c',
  '&:hover': { background: 'rgba(0,0,0,0.05)' },
  '& .MuiSvgIcon-root': {
    fontSize: 18,
  },
});

// The whole media-plus-identity block is one click target, and it is the part
// of the card that flexes.
export const CARD_LINK_SX = {
  flex: '1 1 auto',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  overflow: 'hidden',
};

export const CARD_ACTIONS_SX = { flexShrink: 0, padding: 0.25, minHeight: 'auto' };
